import React, { FunctionComponent, useRef, useState } from 'react';
import { PageContainer } from '@ant-design/pro-layout';
import {
    Button,
    Col,
    Collapse,
    ConfigProvider,
    Dropdown,
    Menu,
    Modal,
    notification,
    Row,
    Spin,
    Tabs,
    Tooltip,
    Typography,
} from 'antd';
import './../Welcome.css';
import './SurveyOverview.css';
import { IAppComponentProps } from '../../components';
import { AgGridReact } from 'ag-grid-react';
import { GridApi } from 'ag-grid-community';
import { SURVEY_MANAGEMENT_TABLE_HEADERS } from './SurveyUtils';
import { QUERY_SURVEY_MANAGEMENT_DATA } from './queries';
import SurveyManagementForm from './SurveyManagementForm';
import client from '../../config/GraphQLApolloClient';
import {
    DownOutlined,
    EyeOutlined,
    FolderViewOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import RenderIfEntitled from '../../components/EntitlementComponents';
import { getAgGridLocalization, PersonalizeGridView } from '../info-portal/components/grid/GridUtil';
import { FormInstance } from 'antd/lib/form/Form';
import enUS from 'antd/lib/locale/en_US';
import deDe from 'antd/lib/locale/de_DE';
import { useAppDispatch } from '../../main/hooks';
import { setSurveyMode, updateSelectedSurveyManagement } from './IStoredSurveySlice';
import { MODE } from './IStoredSurvey';
import { getI18n } from '../../utils/Utils';
import TabPane from 'antd/es/tabs/TabPane';
import styles from '../../styles/scss/portal.scss';
import moduleStyles from '../documents/styling/document.module.css';

const SurveyManagement: FunctionComponent<IAppComponentProps> = (props) => {
    const dispatch = useAppDispatch();
    const currentUser = props.currentUser;
    const [gridApi, setGridApi] = useState<GridApi>(undefined);
    const [isLoading, setLoading] = useState(false);
    const [surveyManagementData, setSurveyManagementData] = useState(undefined);
    const [_flag, setFlag] = useState<boolean>(false);
    const intl = useIntl();
    const navigate = useNavigate();
    const formInstance = useRef<FormInstance<any>>(null);

    const updateFormInstance = (form: FormInstance<any>) => {
        formInstance.current = form;
    };

    const gridView = useRef<PersonalizeGridView>(
        new PersonalizeGridView('survey_management_screen', SURVEY_MANAGEMENT_TABLE_HEADERS(intl, currentUser))
    );

    const onGridReady = (params) => {
        setGridApi(params.api);
        params.api.sizeColumnsToFit();
        gridView.current.initPersonalizedView(params.api);
    };

    const fetchData = (input) => {
        setLoading(true);
        client
            .query({
                query: QUERY_SURVEY_MANAGEMENT_DATA,
                variables: { surveyCriteriaType: input },
                fetchPolicy: 'network-only',
            })
            .then((response) => {
                setSurveyManagementData(response.data.getSurveyManagementList);
                setLoading(false);
            })
            .catch((err) => {
                notification.error(err);
                setLoading(false);
            });
        setLoading(true);
    };

    const onSearch = (values) => {
        const surveyManagementInputs: any = {
            partnerNo: values.partnerNo === undefined ? null : values.partnerNo,
            partnerName: values.partnerName === undefined ? null : values.partnerName,
            companyCode: values.companyCode === undefined ? null : values.companyCode,
            email: values.email === undefined ? null : values.email,
            surveyStatusId: values.surveyStatus === undefined ? null : parseFloat(values.surveyStatus),
            surveyId: values.surveyId !== undefined ? parseFloat(values.surveyId) : null,
            filterOption: values.filterOption !== undefined ? values.filterOption : null,
        };

        fetchData(surveyManagementInputs);
    };

    const onViewClick = () => {
        const selectedRow = gridApi.getSelectedRows();
        if (selectedRow.length > 0) {
            dispatch(setSurveyMode(MODE.OVERVIEW));
            dispatch(updateSelectedSurveyManagement(JSON.stringify(selectedRow)));
            //window.localStorage.setItem('SurveyManagementView', 'selected');
            //window.localStorage.setItem('selectedSurveyManagement', JSON.stringify(selectedRow));
            navigate('/survey/survey-overview');
        } else {
            Modal.warning({
                title: intl.formatMessage({
                    id: 'management-viewsurvey-link-title',
                }),
                content: intl.formatMessage({
                    id: 'management-please-select-survey',
                }),
            });
        }
    };

    const reload = () => setFlag((prev) => !prev);

    const performSearch = () => {
        if (formInstance.current) {
            formInstance.current.submit();
        }
    };
    const resetFields = () => {
        if (formInstance.current) {
            formInstance.current.resetFields();
        }
    };

    const onSave = (_: any) => {
        gridView.current.savePersonalizedView();
        reload();
        notification.info({ message: intl.formatMessage({ id: 'report-result-preferences-saved' }) });
    };

    const onApplyPerson = (_: any) => {
        gridView.current.applyPersonalizedView();
    };

    const onReset = (_: any) => {
        gridApi.setFilterModel(null);
        gridView.current.reset();
    };

    const downIcon = () => <DownOutlined />;

    const menu = (
        <Menu>
            <Menu.Item key="3" disabled={!gridView.current.hasPersonalizedView()}>
                <UserOutlined onClick={onApplyPerson} />
                <a data-testid={'info-portal-personal'} onClick={onApplyPerson}>
                    {intl.formatMessage({ id: 'info-portal-personal', defaultMessage: 'Personal' })}
                </a>
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="4">
                <SaveOutlined onClick={onSave} />

                <a data-testid={'info-portal-save'} onClick={onSave}>
                    {intl.formatMessage({ id: 'info-portal-save', defaultMessage: 'Save' })}
                </a>
            </Menu.Item>
        </Menu>
    );

    return (
        <>
            <ConfigProvider locale={localStorage.getItem('locale') === 'en' ? enUS : deDe}>
                <PageContainer
                    title={false}
                    content={''}
                    extraContent={''}
                    className={styles.users}
                    style={{ background: 'white' }}
                >
                    <Tabs defaultActiveKey={'1'}>
                        <TabPane
                            tab={getI18n('management-page-title', 'Survey Management', intl)}
                            key="1"
                            id="survey-management-tab"
                        >
                            <Spin spinning={isLoading} size={'large'}>
                                <Collapse defaultActiveKey={'panel-key-1'}>
                                    <Collapse.Panel
                                        key="panel-key-1"
                                        header={getI18n('Show-Description', 'Show Description', intl)}
                                    >
                                        <Typography.Title level={5} className={'mb10'}>
                                            {getI18n('management-page-content', '', intl)}
                                        </Typography.Title>
                                    </Collapse.Panel>
                                </Collapse>

                                <Col md={24} sm={12} xs={24}>
                                    <SurveyManagementForm
                                        onSearch={onSearch}
                                        bubbleFormInstance={updateFormInstance}
                                        currentUser={currentUser}
                                        intl={props.intl}
                                        distinctEntitlements={props.distinctEntitlements}
                                        hideButtons={true}
                                    />

                                    <div style={{ marginBottom: '20px', marginLeft: '10px' }}>
                                        <Tooltip title={intl.formatMessage({ id: 'info-portal-search' })}>
                                            <Button
                                                style={{ marginRight: '10px' }}
                                                data-testid="info-portal-search-button"
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={performSearch}
                                            />
                                        </Tooltip>

                                        <Tooltip title={intl.formatMessage({ id: 'do-clear' })}>
                                            <Button
                                                style={{ marginRight: '10px' }}
                                                data-testid="info-portal-search-button"
                                                type="primary"
                                                icon={<ReloadOutlined />}
                                                onClick={resetFields}
                                            />
                                        </Tooltip>

                                        <RenderIfEntitled
                                            entitlementToCheck="display@survey"
                                            intl={intl}
                                            distinctEntitlements={props.distinctEntitlements}
                                        >
                                            <Tooltip
                                                title={intl.formatMessage({
                                                    id: 'management-viewsurvey-link-title',
                                                    defaultMessage: 'View Survey',
                                                })}
                                                key={'viewSurvey'}
                                            >
                                                <Button
                                                    style={{ marginRight: '10px' }}
                                                    data-testid="info-portal-search-button"
                                                    type="primary"
                                                    icon={<FolderViewOutlined />}
                                                    onClick={onViewClick}
                                                />
                                            </Tooltip>
                                        </RenderIfEntitled>
                                    </div>

                                    <Row gutter={24} style={{ paddingTop: '10px', marginBottom: '20px', marginLeft: '2px' }}>
                                        <Col style={{ paddingRight: '5px' }}>
                                            <Dropdown.Button
                                                data-testid={'view-result-customization'}
                                                overlay={menu}
                                                trigger={['click']}
                                                icon={downIcon()}
                                                size={'middle'}
                                            >
                                                <EyeOutlined />
                                                {intl.formatMessage({
                                                    id: 'info-portal-view',
                                                    defaultMessage: 'View',
                                                })}
                                            </Dropdown.Button>
                                        </Col>
                                        <Col style={{ paddingRight: '5px' }}>
                                            <Tooltip title={intl.formatMessage({ id: 'reset-filter' })}>
                                                <Button
                                                    data-testid={'view-result-customization-reset'}
                                                    onClick={onReset}
                                                    size={'middle'}
                                                    icon={
                                                        <ReloadOutlined
                                                            className={moduleStyles.simpleFlipRefresh}
                                                        />
                                                    }
                                                >
                                                    {intl.formatMessage({ id: 'info-portal-reset' })}
                                                </Button>
                                            </Tooltip>
                                        </Col>
                                    </Row>

                                    <div className="pl15 ag-theme-balham" style={{ height: '400px' }}>
                                        <AgGridReact
                                            defaultColDef={{
                                                enableValue: true,
                                                sortable: true,
                                                resizable: true,
                                            }}
                                            columnDefs={gridView.current.getView()}
                                            rowData={surveyManagementData === undefined ? [] : surveyManagementData}
                                            sideBar={false}
                                            rowClass="agGridRows"
                                            localeText={getAgGridLocalization(intl)}
                                            suppressMenuHide={true}
                                            debug={false}
                                            onGridSizeChanged={() => gridApi.sizeColumnsToFit()}
                                            onGridReady={onGridReady}
                                            rowSelection={'single'}
                                            pagination={true}
                                            paginationPageSize={15}
                                            suppressRowClickSelection={true}
                                            enableBrowserTooltips={true}
                                        />
                                    </div>
                                </Col>
                            </Spin>
                        </TabPane>
                    </Tabs>
                </PageContainer>
            </ConfigProvider>
        </>
    );
};
export default SurveyManagement;
